// src/components/UpgradeModal.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import type { PlanKey } from "../config/plans";

function prettyPlanName(plan: PlanKey): string {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

export type UpgradeModalProps = {
  open: boolean;
  onClose: () => void;

  title: string;
  subtitle?: string;

  requiredPlan: PlanKey;

  /**
   * Recomendación: estandariza keys:
   * - "brand_piece"
   * - "brand_persist"
   * - "brand_advanced_fields"
   * - "brand_multiple" (si lo usas)
   * - "resolution:1080p" (si lo usas en creator)
   */
  feature: string;
  piece?: string;

  bullets?: string[];

  from?: string; // ej: "/brand" o "/brand/kit"
};

export default function UpgradeModal({
  open,
  onClose,
  title,
  subtitle,
  requiredPlan,
  feature,
  piece,
  bullets,
  from = "/brand",
}: UpgradeModalProps) {
  const navigate = useNavigate();

  if (!open) return null;

  const goUpgrade = () => {
    const params = new URLSearchParams();
    params.set("feature", feature);
    params.set("required", requiredPlan);
    if (piece) params.set("piece", piece);
    if (from) params.set("from", from);

    // ✅ Mejor UX: NO reemplazar historia, para que Back funcione natural
    navigate(`/upgrade?${params.toString()}`, { replace: false });
  };

  const list =
    bullets?.length
      ? bullets
      : [
          "Acceso inmediato a esta capacidad",
          "Mejor consistencia para campañas",
          "Menos trabajo manual y más velocidad",
        ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0b0b12] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">
              Upgrade recomendado
            </div>
            <h3 className="text-xl font-semibold mt-2">{title}</h3>

            <p className="text-sm text-gray-400 mt-2">
              {subtitle ? (
                subtitle
              ) : (
                <>
                  Esta función está disponible desde el plan{" "}
                  <span className="text-gray-100 font-semibold">
                    {prettyPlanName(requiredPlan)}
                  </span>
                  .
                </>
              )}
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-sm text-gray-200 font-semibold mb-2">
            Lo que desbloqueas
          </div>
          <ul className="text-sm text-gray-300 space-y-2">
            {list.map((x) => (
              <li key={x}>• {x}</li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-sm"
          >
            Seguir con mi plan
          </button>

          <button
            onClick={goUpgrade}
            className="px-4 py-2 rounded-lg border border-indigo-400/30 bg-indigo-500/15 hover:bg-indigo-500/20 transition text-sm"
          >
            Actualizar a {prettyPlanName(requiredPlan)}
          </button>
        </div>
      </div>
    </div>
  );
}

